import { z } from '@hono/zod-openapi'
import { katakanaZ } from '../lib/validation_lib'

// id
export const idZ = z.number().openapi({ description: '物件ID', example: 1 })
export type id = z.infer<typeof idZ>

// ownerId
export const ownerIdZ = z
  .string()
  .openapi({ description: '物件を所有するオーナーID', example: 'uuid-1234' })
export type ownerId = z.infer<typeof ownerIdZ>

// name: 物件名
export const nameZ = z
  .string()
  .min(1)
  .openapi({ description: '物件名', example: '新規物件' })
export type name = z.infer<typeof nameZ>

// nameKana: 物件名カナ
export const nameKanaZ = katakanaZ.openapi({
  description: '物件名カナ',
  example: 'シンキブッケン',
})
export type nameKana = z.infer<typeof nameKanaZ>

// upperZip: 郵便番号 上3桁
export const upperZipZ = z
  .string()
  .min(1)
  .openapi({ description: '郵便番号の上3桁', example: '123' })
export type upperZip = z.infer<typeof upperZipZ>

// lowerZip: 郵便番号 下4桁
export const lowerZipZ = z
  .string()
  .min(1)
  .openapi({ description: '郵便番号の下4桁', example: '4567' })
export type lowerZip = z.infer<typeof lowerZipZ>

// prefecture: 都道府県
export const prefectureZ = z
  .string()
  .min(1)
  .openapi({ description: '都道府県', example: '東京都' })
export type prefecture = z.infer<typeof prefectureZ>

// prefectureKana: 都道府県カナ
export const prefectureKanaZ = katakanaZ.openapi({
  description: '都道府県カナ',
  example: 'トウキョウト',
})
export type prefectureKana = z.infer<typeof prefectureKanaZ>

// city: 市区町村
export const cityZ = z
  .string()
  .min(1)
  .openapi({ description: '市区町村', example: '渋谷区' })
export type city = z.infer<typeof cityZ>

// cityKana: 市区町村カナ
export const cityKanaZ = katakanaZ.openapi({
  description: '市区町村カナ',
  example: 'シブヤク',
})
export type cityKana = z.infer<typeof cityKanaZ>

// town: 町名
export const townZ = z
  .string()
  .min(1)
  .openapi({ description: '町名', example: '恵比寿' })
export type town = z.infer<typeof townZ>

// townKana: 町名カナ（カタカナ、数字、ハイフン、スペース）
export const townKanaZ = z
  .string()
  .min(1)
  .regex(/^[ァ-ヴー0-9０-９\-− 　]+$/, {
    message:
      'カタカナ、数字（半角・全角）、ハイフン（半角・全角）、スペースのみ入力可能です',
  })
  .openapi({ description: '町名カナ', example: 'エビス' })
export type townKana = z.infer<typeof townKanaZ>

// district: 丁目・番地など
export const districtZ = z
  .string()
  .min(1)
  .regex(/^[0-9０-９\p{P}\p{S}\s]+$/u)
  .openapi({ description: '丁目・番地・建物番号', example: '4-3-1' })
export type district = z.infer<typeof districtZ>

// districtKana: optional legacy field kept for API compatibility
export const districtKanaZ = z
  .string()
  .openapi({ description: '丁目・番地・建物番号カナ', example: '' })
export type districtKana = z.infer<typeof districtKanaZ>

// builtYear: 築年(西暦)
export const builtYearZ = z
  .number()
  .openapi({ description: '築年（西暦）', example: 2018 })
export type builtYear = z.infer<typeof builtYearZ>

// structure: 構造
/**
 * 用法とは
①木造
「木造」の建物。
②鉄骨造
「軽量鉄骨造」「鉄骨造」「重量鉄骨造」「HPC（鉄骨プレキャストコンクリート造）」「ALC（軽量気泡コンクリート）」の建物。
③RC造/SRC造/PC造
「RC（鉄筋コンクリート造）」「SRC（鉄骨鉄筋コンクリート造）」「PC（プレキャストコンクリート造）」の建物。
④その他
「ブロック」「鉄筋ブロック造」「CFT（コンクリート充鎮鋼管造）」の建物。
 */

export const PROPERTY_STRUCTURE_MOKUZOU = '1'
export const PROPERTY_STRUCTURE_TETKOTU = '2'
export const PROPERTY_STRUCTURE_RCSRCPC = '3'
export const PROPERTY_STRUCTURE_SONOTA = '4'
export const PROPERTY_STRUCTURES = [
  { key: PROPERTY_STRUCTURE_MOKUZOU, value: '木造' },
  { key: PROPERTY_STRUCTURE_TETKOTU, value: '鉄骨造' },
  { key: PROPERTY_STRUCTURE_RCSRCPC, value: 'RC造/SRC造/PC造' },
  { key: PROPERTY_STRUCTURE_SONOTA, value: 'その他' },
] as const
export const structureZ = z
  .enum([
    PROPERTY_STRUCTURE_MOKUZOU,
    PROPERTY_STRUCTURE_TETKOTU,
    PROPERTY_STRUCTURE_RCSRCPC,
    PROPERTY_STRUCTURE_SONOTA,
  ])
  .openapi({
    description:
      '建物構造（1:木造 / 2:鉄骨造 / 3:RC造・SRC造・PC造 / 4:その他）',
    example: PROPERTY_STRUCTURE_MOKUZOU,
  })
export type structure = z.infer<typeof structureZ>

// type: 用法
/**
 * 用法とは
 * ①専用住宅：住居のみの用途に使用される建物
 * ②共同住宅：1世帯の生活単位となる戸室を2以上有し、各戸室または建物に付属して各世帯が炊事を行う設備がある建物（マンション、アパート等）
 * ③併用住宅：住居の用途と、店舗・事務所等の事業の用途に併用される建物
 */
export const PROPERTY_TYPE_SENYOU = '1'
export const PROPERTY_TYPE_KYOUDOU = '2'
export const PROPERTY_TYPE_HEIYOU = '3'
export const PROPERTY_TYPES = [
  { key: PROPERTY_TYPE_SENYOU, value: '専用住宅' },
  { key: PROPERTY_TYPE_KYOUDOU, value: '共同住宅' },
  { key: PROPERTY_TYPE_HEIYOU, value: '併用住宅' },
] as const
export const typeZ = z
  .enum([PROPERTY_TYPE_SENYOU, PROPERTY_TYPE_KYOUDOU, PROPERTY_TYPE_HEIYOU])
  .openapi({
    description: '用法（1:専用住宅 / 2:共同住宅 / 3:併用住宅）',
    example: PROPERTY_TYPE_SENYOU,
  })
export type type = z.infer<typeof typeZ>

// floor: 階数
export const floorZ = z
  .number()
  .openapi({ description: '建物の階数', example: 4 })
export type floor = z.infer<typeof floorZ>

export const PROPERTY_OWNERSHIP_TYPE_WHOLE = 'WHOLE'
export const PROPERTY_OWNERSHIP_TYPE_CONDO = 'CONDO'
export const ownershipTypeZ = z
  .enum([PROPERTY_OWNERSHIP_TYPE_WHOLE, PROPERTY_OWNERSHIP_TYPE_CONDO])
  .openapi({
    description: '物件の所有形態（WHOLE: 一棟所有 / CONDO: 区分所有）',
    example: PROPERTY_OWNERSHIP_TYPE_WHOLE,
  })
export type ownershipType = z.infer<typeof ownershipTypeZ>

// managementCompanyName: 建物管理会社名
export const managementCompanyNameZ = z
  .string()
  .openapi({ description: '建物管理会社名', example: '管理会社A' })
export type managementCompanyName = z.infer<typeof managementCompanyNameZ>

// managementCompanyPhone: 建物管理会社電話番号(ハイフンなし)
export const managementCompanyPhoneZ = z.string().openapi({
  description: '建物管理会社の電話番号（ハイフンなし）',
  example: '0312345678',
})
export type managementCompanyPhone = z.infer<typeof managementCompanyPhoneZ>

// adminMemo: 管理メモ
export const adminMemoZ = z
  .string()
  .openapi({ description: '管理者用メモ（社内用）', example: 'メモ' })
export type adminMemo = z.infer<typeof adminMemoZ>

// note: 備考
export const noteZ = z
  .string()
  .openapi({ description: '備考', example: '備考メモ' })
export type note = z.infer<typeof noteZ>

// createdAt: 作成日時
export const createdAtStringZ = z.string().openapi({
  description: '作成日時（ISO8601）',
  example: '2024-01-01T00:00:00.000Z',
})
export type createdAtString = z.infer<typeof createdAtStringZ>

export const createdAtDateZ = z
  .date()
  .openapi({ example: '2024-01-01T00:00:00.000Z' })
export type createdAtDate = z.infer<typeof createdAtDateZ>

// updatedAt: 更新日時
export const updatedAtStringZ = z.string().openapi({
  description: '更新日時（ISO8601）',
  example: '2024-01-01T00:00:00.000Z',
})
export type updatedAtString = z.infer<typeof updatedAtStringZ>

export const updatedAtDateZ = z
  .date()
  .openapi({ example: '2024-01-01T00:00:00.000Z' })
export type updatedAtDate = z.infer<typeof updatedAtDateZ>

// deletedAt: 削除日時
export const deletedAtStringZ = z
  .string()
  .nullable()
  .openapi({ description: '削除日時（ISO8601）。null は未削除', example: null })
export type deletedAtString = z.infer<typeof deletedAtStringZ>

export const deletedAtDateZ = z.date().nullable().openapi({ example: null })
export type deletedAtDate = z.infer<typeof deletedAtDateZ>

// echoesBuildingId（Echoesの建物ID）
export const echoesBuildingIdZ = z.string().nullable().openapi({ example: '1' })
export type echoesBuildingId = z.infer<typeof echoesBuildingIdZ>

// echoesUpdatedAt（取り込み済み Echoes レコードの updated_at。等値比較で洗替要否を判定する）
export const echoesUpdatedAtDateZ = z
  .date()
  .nullable()
  .openapi({ example: null })
export type echoesUpdatedAtDate = z.infer<typeof echoesUpdatedAtDateZ>

// echoesFailedAt（Echoesからの同期に最後に失敗した日時。成功時は null に戻す）
export const echoesFailedAtDateZ = z
  .date()
  .nullable()
  .openapi({ example: null })
export type echoesFailedAtDate = z.infer<typeof echoesFailedAtDateZ>
